// 画板框架模版：叙事框架（与思考模式同源）+ 需求金字塔 + 用户自定义框架
// 注意：必须写全扩展名 —— tests/*.mjs 用原生 Node ESM 直接导入。
import { BOARD_MODES } from './board-modes.js'

// 需求金字塔：6 层，具体到生存级别；每层带具体内容（items 显示在区域里）
const MASLOW_ZONES = [
  { id: 'self', label: '自我实现', items: ['创造', '意义', '成长'], color: '#c9a24d' },
  { id: 'esteem', label: '尊重需求', items: ['认可', '地位', '自尊'], color: '#b0815f' },
  { id: 'love', label: '社交需求', items: ['归属', '友谊', '爱'], color: '#8fa58b' },
  { id: 'safety', label: '安全需求', items: ['稳定', '健康', '保护'], color: '#8b9cb5' },
  { id: 'physio', label: '生理需求', items: ['食物', '水', '睡眠'], color: '#6f7d72' },
  { id: 'survival', label: '生存需求', items: ['呼吸', '温度', '避难'], color: '#4f5d52' },
]

const TRAGEDY9_ZONES = [
  { id: 't-cog', label: '认知层', color: '#c9a24d', items: ['这个人物相信什么？把「信念台词」找出来（威利的「人缘就是全部」、克瑞翁的「城邦至上」）。', '这个信念是被证伪、被蒸发，还是从未存在？三者对应的悲剧形态完全不同。', '他有没有「看见」的时刻？看见了什么——真理、自己的价格，还是什么都没看见？'] },
  { id: 't-act', label: '行动层', color: '#b0815f', items: ['他有没有一个明确的动作指向墙？如果有，那个动作是攻击、绕过、还是跪下？', '他用来反抗的手段，是不是恰好是墙教给他的？（摧毁型的判准）', '如果没有动作，是什么让他留在墙上？惯性、恐惧、还是已经无法想象墙外？'] },
  { id: 't-reify', label: '物化层', color: '#6f7d72', items: ['规则在这部作品里被叫什么名字？（「公司」「制度」「命运」「体面」「考核」）——命名本身就是物化行为。', '有没有一个物件承载了人物的全部价值？（祥子的车、威利的保险单、周朴园的旧家具）找到那个物件，物化分析就有了锚点。', '观众/读者能不能给这场苦难归因？能归因到某个人，是情节剧；归因到系统，是社会学；归不了因，才是悲剧。'] },
]

export const BUILTIN_FRAMEWORKS = [
  { id: 'fw-maslow', name: '需求金字塔', kind: 'pyramid', zones: MASLOW_ZONES },
  ...BOARD_MODES.map((mode) => ({
    id: 'fw-' + mode.id,
    name: mode.label,
    kind: 'columns',
    zones: mode.columns.map((column) => ({ id: column.id, label: column.label, color: column.color })),
  })),
  { id: 'fw-tragedy9', name: '悲剧分析九问', kind: 'columns', zones: TRAGEDY9_ZONES },
]

export function frameworkById(id, userFrameworks = []) {
  if (!id) return null
  return BUILTIN_FRAMEWORKS.find((item) => item.id === id) || (userFrameworks || []).find((item) => item.id === id) || null
}

// 计算区域几何（百分比坐标 0-100）：列布局 / 金字塔布局
export function layoutZones(framework) {
  const zones = framework ? framework.zones || [] : []
  const n = zones.length
  if (!n) return []
  if (framework.kind === 'pyramid') {
    const last = n - 1
    return zones.map((zone, index) => {
      const half = last > 0 ? 8 + (index * 34) / last : 42
      return { ...zone, x: 50 - half, y: 3 + index * (92 / n), w: half * 2, h: 92 / n - 2.5 }
    })
  }
  return zones.map((zone, index) => ({
    ...zone,
    x: 2 + (index * 100) / n,
    y: 3,
    w: 96 / n,
    h: 92,
  }))
}

// 命中测试：世界坐标 (x, y) 落在框架的哪个区域（未命中返回 null）
export function zoneAt(framework, x, y, size = { w: 2400, h: 1600 }) {
  if (!framework) return null
  for (const zone of layoutZones(framework)) {
    const left = (zone.x / 100) * size.w
    const top = (zone.y / 100) * size.h
    const width = (zone.w / 100) * size.w
    const height = (zone.h / 100) * size.h
    if (x >= left && x <= left + width && y >= top && y <= top + height) return zone
  }
  return null
}

// 最近区域命中：掉在区域间隙时，吸附到距离最近的区域（容差内）
export function nearestZoneAt(framework, x, y, size = { w: 2400, h: 1600 }, tolerance = 300) {
  if (!framework) return null
  let best = null
  let bestDistance = Infinity
  for (const zone of layoutZones(framework)) {
    const left = (zone.x / 100) * size.w
    const top = (zone.y / 100) * size.h
    const width = (zone.w / 100) * size.w
    const height = (zone.h / 100) * size.h
    const centerX = left + width / 2
    const centerY = top + height / 2
    const distance = Math.sqrt((x - centerX) * (x - centerX) + (y - centerY) * (y - centerY))
    if (distance < bestDistance) { bestDistance = distance; best = zone }
  }
  return bestDistance <= tolerance ? best : null
}

// 框架自动放大：按放置卡片的包围盒（含边距）计算，不小于基准尺寸
export function frameworkSizeFor(cards, base = { w: 2400, h: 1600 }) {
  let w = base.w
  let h = base.h
  for (const card of cards || []) {
    w = Math.max(w, (card.x || 0) + 280)
    h = Math.max(h, (card.y || 0) + 200)
  }
  return { w: Math.ceil(w / 40) * 40, h: Math.ceil(h / 40) * 40 }
}
